"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import {
  AuthShell,
  authBtnClass,
  authCheckboxClass,
  authInputClass,
  authLabelClass,
  authLinkClass,
} from "~/components/auth/AuthShell";
import { Spinner } from "~/components/display/Spinner";
import { routes } from "~/lib/routes";

type Stage = "login" | "new_password";

// 2026-08-27〜09-01: CognitoのCUSTOM_AUTHフローがNotAuthorizedExceptionになる
// 不具合(AWSサポートで原因判明・prance-id側cognito-triggers/create-auth-challenge.tsで
// 修正済み)のため一時的にfalseにしていたが、修正確認済みのため復旧。
const SSO_SILENT_LOGIN_ENABLED = true;

export default function AuthPage() {
  const t = useTranslations("AuthPage");
  const router = useRouter();
  const searchParams = useSearchParams();
  // セッション切れ等でrequireAuth()から/login?from=<元のパス>へ飛ばされた場合、
  // ログイン成功後は既定のusageページではなく元のページへ戻す。
  const redirectTo = searchParams.get("from") ?? routes.manage.usage;

  const [stage, setStage] = useState<Stage>("login");
  const [pendingEmail, setPendingEmail] = useState("");
  const [pendingSession, setPendingSession] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // prance-idリポジトリ docs/decision-log.md ADR-014(製品間SSO)。マウント時に
  // 一度だけ`prance_id_sso_ticket`Cookieの有無を確認し、有効なら無言でログインを
  // 完了させる。チェック完了までフォームを表示しない(表示→即消滅のちらつきを防ぐ)。
  const [checkingSso, setCheckingSso] = useState(true);

  useEffect(() => {
    // prance-idリポジトリ docs/decision-log.md ADR-014(2026-09-01再訂正)・
    // docs/naming-conventions.md §13。ログアウト直後の1回だけsilent-loginを
    // スキップする(UserMenu.tsxがログアウト時にセットするフラグ)。まだ有効な
    // 共有チケットでログアウトした直後に即座に再ログインしてしまうのを防ぐ —
    // 共有チケット自体は消さないため、他製品経由での再アクセスや時間を置いた
    // 再訪問では通常通りsilent-loginが機能する。
    if (sessionStorage.getItem("prance_id_sso_skip_once")) {
      sessionStorage.removeItem("prance_id_sso_skip_once");
      setCheckingSso(false);
      return;
    }
    if (!SSO_SILENT_LOGIN_ENABLED) {
      setCheckingSso(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/api/auth/silent-login", { method: "POST" });
        if (cancelled) return;
        if (res.ok) {
          router.push(redirectTo);
          router.refresh();
          return;
        }
      } catch {
        // ネットワークエラー等は通常のパスワードフォームへフォールバックする。
      }
      if (!cancelled) setCheckingSso(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [router, redirectTo]);

  async function handleLogin(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const form = e.currentTarget;
    const email = (form.elements.namedItem("email") as HTMLInputElement).value;
    const password = (form.elements.namedItem("password") as HTMLInputElement)
      .value;

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        challenge?: string;
        session?: string;
        email?: string;
        error?: string;
      };

      if (res.ok && data.challenge === "NEW_PASSWORD_REQUIRED") {
        setPendingEmail(data.email ?? email);
        setPendingSession(data.session ?? "");
        setStage("new_password");
        return;
      }
      if (res.ok) {
        router.push(redirectTo);
        router.refresh();
        return;
      }
      setError(
        res.status === 401 ? t("errorInvalidCredentials") : t("errorUnknown"),
      );
    } catch {
      setError(t("errorUnknown"));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleNewPassword(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    const form = e.currentTarget;
    const newPassword = (
      form.elements.namedItem("newPassword") as HTMLInputElement
    ).value;
    const confirmPassword = (
      form.elements.namedItem("confirmPassword") as HTMLInputElement
    ).value;

    if (newPassword !== confirmPassword) {
      setError(t("errorPasswordMismatch"));
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: pendingEmail,
          session: pendingSession,
          newPassword,
        }),
      });
      const data = (await res.json()) as { ok?: boolean; error?: string };

      if (res.ok) {
        router.push(redirectTo);
        router.refresh();
        return;
      }
      if (data.error === "session_expired" || res.status === 401) {
        setStage("login");
        setPendingSession("");
        setError(t("errorSessionExpired"));
      } else if (data.error === "invalid_password") {
        setError(t("errorInvalidPassword"));
      } else {
        setError(t("errorUnknown"));
      }
    } catch {
      setError(t("errorUnknown"));
    } finally {
      setSubmitting(false);
    }
  }

  if (checkingSso) {
    return (
      <AuthShell>
        <div className="flex justify-center py-20">
          <Spinner />
        </div>
      </AuthShell>
    );
  }

  // ── 初回ログイン強制パスワード変更 ─────────────────────────
  if (stage === "new_password") {
    return (
      <AuthShell>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => void handleNewPassword(e)}
        >
          <div>
            <h2 className="mb-2.5 font-medium text-[30px] tracking-[-0.01em]">
              {t("newPasswordTitle")}
            </h2>
            <p className="mb-8.5 text-[14.5px] text-muted leading-[1.6]">
              {t("newPasswordDescription")}
            </p>
          </div>
          <div>
            <label htmlFor="newPassword" className={authLabelClass}>
              {t("newPasswordLabel")}
            </label>
            <input
              id="newPassword"
              name="newPassword"
              type="password"
              required
              minLength={8}
              // biome-ignore lint/a11y/noAutofocus: login form UX
              autoFocus
              autoComplete="new-password"
              className={authInputClass}
            />
          </div>
          <div>
            <label htmlFor="confirmPassword" className={authLabelClass}>
              {t("confirmPasswordLabel")}
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              className={authInputClass}
            />
          </div>
          {error && <p className="m-0 text-error text-label">{error}</p>}
          <button
            type="submit"
            disabled={submitting}
            className={`${authBtnClass} ${submitting ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
          >
            {submitting ? t("submitting") : t("newPasswordSubmit")}
          </button>
        </form>
      </AuthShell>
    );
  }

  // ── サインインフォーム ──────────────────────────────────────
  return (
    <AuthShell>
      <form className="flex flex-col" onSubmit={(e) => void handleLogin(e)}>
        <h2 className="mb-2.5 font-medium text-[30px] tracking-[-0.01em]">
          {t("signIn")}
        </h2>
        <p className="mb-8.5 text-[14.5px] text-muted leading-[1.6]">
          {t("signInDescription")}
        </p>

        <label htmlFor="email" className={authLabelClass}>
          {t("emailLabel")}
        </label>
        <input
          id="email"
          name="email"
          type="email"
          required
          // biome-ignore lint/a11y/noAutofocus: login form UX
          autoFocus
          autoComplete="email"
          placeholder={t("emailPlaceholder")}
          className={`${authInputClass} mb-5.5`}
        />

        <div className="mb-2 flex items-baseline justify-between">
          <label htmlFor="password" className={`${authLabelClass} mb-0`}>
            {t("passwordLabel")}
          </label>
          <Link
            href={routes.auth.forgotPassword}
            tabIndex={-1}
            className={`text-[12.5px] no-underline ${authLinkClass}`}
          >
            {t("forgotPassword")}
          </Link>
        </div>
        <input
          id="password"
          name="password"
          type="password"
          required
          autoComplete="current-password"
          placeholder={t("passwordPlaceholder")}
          className={`${authInputClass} mb-5`}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.nativeEvent.isComposing) {
              e.currentTarget.form?.requestSubmit();
            }
          }}
        />

        <label className="mb-7 flex cursor-pointer items-center gap-2.25 text-body text-muted">
          <input
            type="checkbox"
            name="rememberMe"
            className={`m-0 size-4 cursor-pointer ${authCheckboxClass}`}
          />
          <span>{t("rememberMe")}</span>
        </label>

        {error && <p className="mb-3 text-error text-label">{error}</p>}

        <button
          type="submit"
          disabled={submitting}
          className={`${authBtnClass} ${submitting ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
        >
          {submitting ? t("submitting") : t("signInButton")}
        </button>

        <p className="mt-6 text-center text-[12.5px] text-placeholder">
          {t("contactAdmin")}
        </p>
      </form>
    </AuthShell>
  );
}
